import { cn } from '@/lib/utils';

/**
 * Inline stacked bar showing two-segment proportions (open vs closed,
 * etc.). Renders as a single thin pill split by % so the eye reads
 * "what fraction of this row's work is still open" at a glance —
 * faster than comparing two number columns side by side.
 *
 * Used by tasks-by-label and tasks-by-assignee tiles. Both widths
 * default to 0 when total is 0, leaving the track empty rather than
 * dividing by zero.
 */
export function StackedBar({
  left,
  right,
  leftClassName = 'bg-warning',
  rightClassName = 'bg-success',
  trackClassName = 'bg-muted',
  height = 'h-2',
  className,
}: {
  /** Left-segment count (e.g. open). */
  left: number;
  /** Right-segment count (e.g. closed). */
  right: number;
  leftClassName?: string;
  rightClassName?: string;
  trackClassName?: string;
  /** Tailwind height class. Defaults to `h-2` to read as data, not as button. */
  height?: string;
  className?: string;
}): React.ReactElement {
  const total = left + right;
  const leftPct = total === 0 ? 0 : (left / total) * 100;
  const rightPct = total === 0 ? 0 : (right / total) * 100;

  return (
    <div
      className={cn('flex w-full overflow-hidden rounded-full', height, trackClassName, className)}
      role="img"
      aria-label={`${left} open, ${right} closed`}
    >
      <div className={cn('h-full', leftClassName)} style={{ width: `${leftPct}%` }} />
      <div className={cn('h-full', rightClassName)} style={{ width: `${rightPct}%` }} />
    </div>
  );
}
